import React, { useEffect, useState } from "react"
import { Search } from "lucide-react"
import CaseDetails from "../components/CaseDetails"
import LoadMore from "../components/LoadMore"

const PAGE_SIZE = 24

const filters = [
  { value: "ALL", label: "All" },
  { value: "CS", label: "CS" },
  { value: "RUST", label: "RUST" },
]

const BestCasesPage = () => {
  const [tab, setTab] = useState("ALL")
  const [cases, setCases] = useState([])
  const [searchQuery, setSearchQuery] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [sortBy, setSortBy] = useState("rtp")
  const [page, setPage] = useState(1)
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  })
  const [retryKey, setRetryKey] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setPage(1)
      setDebouncedSearch(searchQuery.trim())
    }, 300)

    return () => window.clearTimeout(timeout)
  }, [searchQuery])

  useEffect(() => {
    const controller = new AbortController()

    const fetchBestCases = async () => {
      setLoading(true)
      setError("")

      try {
        const base = process.env.REACT_APP_API_URL
        const params = new URLSearchParams({
          page: String(page),
          pageSize: String(PAGE_SIZE),
          top: "100",
          sort: sortBy,
        })

        if (tab !== "ALL") params.set("game", tab)
        if (debouncedSearch) params.set("search", debouncedSearch)

        const res = await fetch(`${base}/cases/best?${params.toString()}`, {
          signal: controller.signal,
        })

        if (!res.ok) {
          throw new Error(`Request failed with status ${res.status}`)
        }

        const data = await res.json()
        const nextCases = Array.isArray(data.cases) ? data.cases : []
        setCases((currentCases) => {
          if (page === 1) return nextCases

          const uniqueCases = new Map(
            [...currentCases, ...nextCases].map((item) => [
              item._id || `${item.Title}-${item.Website}-${item.subpageLinks}`,
              item,
            ])
          )
          return [...uniqueCases.values()]
        })

        const nextPagination = data.pagination || {
          page: 1,
          pageSize: PAGE_SIZE,
          total: 0,
          totalPages: 0,
        }
        setPagination(nextPagination)

        if (nextPagination.page !== page) {
          setPage(nextPagination.page)
        }
      } catch (err) {
        if (err.name === "AbortError") return

        console.error("Failed to fetch best cases:", err)
        if (page === 1) setCases([])
        setError("We couldn't load the best cases. Please try again later.")
      } finally {
        if (!controller.signal.aborted) setLoading(false)
      }
    }

    fetchBestCases()

    return () => controller.abort()
  }, [tab, page, sortBy, debouncedSearch, retryKey])

  const handleLoadMore = () => {
    if (loading || pagination.page >= pagination.totalPages) return
    const nextPage = pagination.page + 1
    if (nextPage === page) {
      setRetryKey((currentKey) => currentKey + 1)
    } else {
      setPage(nextPage)
    }
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <header className="relative mb-10 text-center">
        <h1 className="mb-4 font-display text-4xl font-bold bg-gradient-to-r from-[#FF6B00] via-[#FF9A3D] to-[#FFD700] bg-clip-text text-transparent drop-shadow-glow sm:text-5xl">
          Best Cases
        </h1>
        <p className="mx-auto max-w-2xl text-blue-100/70">
          Compare the top 100 CS and Rust cases ranked by RTP.
        </p>
      </header>

      <div className="mb-10 flex justify-center">
        <div className="flex gap-2 rounded-2xl border border-[#2A2D31]/50 bg-[#1A1D21]/40 p-2 backdrop-blur-sm">
          {filters.map((filter) => (
            <button
              key={filter.value}
              type="button"
              onClick={() => {
                setTab(filter.value)
                setPage(1)
              }}
              className={`rounded-xl px-5 py-2 text-sm font-medium transition-all ${
                tab === filter.value
                  ? "!bg-gradient-to-r from-[#FF6B00] to-[#FF9A3D] text-white shadow-[0_6px_18px_rgba(255,107,0,0.18)]"
                  : "!bg-[#0F1215]/80 text-blue-100/80 border border-[#2A2D31]/50 hover:border-[#FF6B00]/50"
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      <section>
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="font-display text-3xl font-semibold text-white">
              {tab === "ALL" ? "Top Cases" : `Top ${tab} Cases`}
            </h2>
            <p className="mt-1 text-sm text-blue-100/60">
              Ranked by highest return to player.
            </p>
          </div>

          {!loading && !error && (
            <span className="w-fit rounded-full border border-[#2A2D31]/60 bg-[#1A1D21]/50 px-3 py-1 text-sm text-blue-100/70">
              {pagination.total} {pagination.total === 1 ? "result" : "results"}
            </span>
          )}
        </div>

        <div className="mb-8 flex flex-col gap-4 rounded-2xl border border-[#2A2D31]/50 bg-[#1A1D21]/40 p-4 shadow-[0_8px_30px_rgba(0,0,0,0.12)] backdrop-blur-sm md:flex-row">
          <label className="relative flex-1">
            <span className="sr-only">Search best cases</span>
            <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-blue-100/40" />
            <input
              type="search"
              placeholder="Search cases..."
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              className="w-full rounded-xl border border-[#2A2D31]/60 bg-[#0F1215]/80 py-3 pl-10 pr-4 text-sm text-white outline-none transition-colors placeholder:text-blue-100/40 focus:border-[#FF6B00]/60 focus:ring-2 focus:ring-[#FF6B00]/10"
            />
          </label>

          <label className="relative md:w-56">
            <span className="sr-only">Sort best cases</span>
            <select
              value={sortBy}
              onChange={(event) => {
                setSortBy(event.target.value)
                setPage(1)
              }}
              className="w-full appearance-none rounded-xl border border-[#2A2D31]/60 bg-[#0F1215]/80 px-4 py-3 pr-10 text-sm text-white outline-none transition-colors focus:border-[#FF6B00]/60 focus:ring-2 focus:ring-[#FF6B00]/10"
            >
              <option value="rtp">Highest RTP</option>
              <option value="name">Name (A-Z)</option>
              <option value="price">Price (Low to High)</option>
              <option value="price-desc">Price (High to Low)</option>
            </select>
            <svg
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
              className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-blue-100/50"
            >
              <path
                fillRule="evenodd"
                d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                clipRule="evenodd"
              />
            </svg>
          </label>
        </div>

        {error && cases.length === 0 ? (
          <div className="rounded-2xl border border-red-400/20 bg-red-400/5 px-6 py-12 text-center text-red-100/80">
            {error}
          </div>
        ) : loading && cases.length === 0 ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
            {[0, 1, 2, 3, 4, 5, 6, 7].map((item) => (
              <div
                key={item}
                className="h-96 animate-pulse rounded-2xl border border-[#2A2D31]/40 bg-[#1A1D21]/40"
              />
            ))}
          </div>
        ) : cases.length > 0 ? (
          <>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
              {cases.map((item, index) => (
                <CaseDetails
                  key={item._id || `${item.Title}-${index}`}
                  image={item.Image}
                  title={item.Title}
                  website={item.Website}
                  price={item.Price}
                  rtp={item.RTP}
                  game={item.Game || item.game}
                  subpageLinks={item.subpageLinks}
                />
              ))}
            </div>

            {error && (
              <p className="mt-6 text-center text-sm text-red-200/80">{error}</p>
            )}

            <LoadMore
              currentItems={cases.length}
              totalItems={pagination.total}
              loading={loading}
              onLoadMore={handleLoadMore}
            />
          </>
        ) : (
          <div className="rounded-2xl border border-[#2A2D31]/50 bg-[#0F1215]/60 px-6 py-14 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#FF6B00]/10 text-[#FF9A3D]">
              <Search className="h-5 w-5" />
            </div>
            <h3 className="font-display text-lg font-semibold text-white">No cases found</h3>
            <p className="mt-1 text-sm text-blue-100/60">Try a different search or game filter.</p>
          </div>
        )}
      </section>
    </div>
  )
}

export default BestCasesPage
